// Links that leave the widget for the PUBLIC help center (help.acme.com /
// <project>.gleap.help) have to carry the visitor's identity themselves.
//
// The help center reads it from the `gleapId` / `gleapHash` query params:
// HelpCenterApp's `proxy.ts` turns them into the `x-gleap-id` / `x-gleap-hash`
// headers, `apiFetch` forwards those to the API and exchanges them for a
// session cookie. There is no other channel — the widget runs on the customer's
// domain, so it shares no cookie with the help center, and opening a link in
// the system browser (native SDKs) starts from a blank jar regardless.
//
// Arrive without them and the visitor is anonymous, so the API's audience
// filter drops every article whose own — or whose parent collection's — target
// audience is anything but "all", and the page renders "Content Not Found" for
// an article that reads perfectly inside the widget (#143576).

export interface HelpCenterIdentity {
  gleapId?: string;
  gleapHash?: string;
}

export interface HelpCenterArticleUrlOptions {
  // `getHelpCenterDomain()` — null when the project has no help center domain.
  domain?: string | null;
  // The article's `docId`, or its id as a fallback. The help center reads it
  // back with `params.article.split("-")[0]`, so it must lead the last path
  // segment and stay free of dashes.
  articleId?: string | number | null;
  title?: string | null;
  // The article's canonical `url` from the server. It already points at the
  // site that serves the article (which may not be the default help center),
  // so it wins over a URL built from `domain`.
  url?: string | null;
  session?: HelpCenterIdentity | null;
}

// The project's default help center host. The base domain is regional
// ("gleap.help" / "us.gleap.help") and comes with the widget config; servers
// that predate the field don't send it, which always means "gleap.help".
export const helpCenterDefaultHost = (config: {
  defaultDomainHelpCenter?: string;
  helpCenterBaseDomain?: string;
}): string =>
  `${config.defaultDomainHelpCenter}.${config.helpCenterBaseDomain || "gleap.help"}`;

// `undefined` reaches these fields as the literal string in the wild, which is
// why the help center guards against it too (HelpCenterApp `apiFetch`). Sending
// it identifies nobody while looking like a session, so treat it as absent.
const isUsableIdentity = (value?: string | null): value is string =>
  typeof value === "string" && value.length > 0 && value !== "undefined";

// Stamps the current session onto an outbound help center URL, replacing any
// identity the URL already carries (an extended-view page URL keeps the params
// of whoever loaded it, which may be a stale session).
export const withSessionIdentity = (
  url: string,
  session?: HelpCenterIdentity | null,
): string => {
  const gleapId = session?.gleapId;
  const gleapHash = session?.gleapHash;

  if (!isUsableIdentity(gleapId) || !isUsableIdentity(gleapHash)) {
    return url;
  }

  try {
    const parsed = new URL(url);
    parsed.searchParams.set("gleapId", gleapId);
    parsed.searchParams.set("gleapHash", gleapHash);
    return parsed.toString();
  } catch (_) {
    // Not an absolute URL — nothing safe to append to.
    return url;
  }
};

// The public URL of a help center article, identity included. Returns null when
// there is nothing openable, so callers can hide the button instead of sending
// the visitor to `https://null/articles/...`.
export const buildHelpCenterArticleUrl = ({
  domain,
  articleId,
  title,
  url,
  session,
}: HelpCenterArticleUrlOptions): string | null => {
  if (typeof url === "string" && /^https?:\/\//i.test(url)) {
    return withSessionIdentity(url, session);
  }

  if (!domain) {
    return null;
  }

  const id = articleId === null || articleId === undefined ? "" : `${articleId}`;
  if (id.length === 0) {
    return null;
  }

  // The slug after the id is cosmetic: the help center resolves the article
  // from the id and redirects to its own canonical slug if this one differs.
  const slug = encodeURIComponent(title?.toLowerCase() ?? "");

  return withSessionIdentity(`${domain}/articles/${id}-${slug}`, session);
};
